// @ts-ignore
import { store, setLocaleLanguageCode } from "../../../../acc-boot/acc-boot.js";
import { fetchDedupe } from "fetch-dedupe";

/**
 *
 * translation() - mixin class that works in conjunction with `acc-boot` to
 * translate and interpolate strings
 *
 * ## Importing and Extending
 *
 * In typical use, you import and use this to extend your base class. This works well
 * with Polymer 3 as well as litElement:
 *
 * _Polymer 3_
```
import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {translation} from './translation.js';

class MySampleComponent extends translation(PolymerElement) {
  // ...
}
window.customElements.define('my-sample-component', MySampleComponent);
```
* _litElement:_
```
import {LitElement} from 'lit-element';
import {translation} from './translation.js';

class MySampleComponent extends translation(LitElement) {
  // ...
}
window.customElements.define('my-sample-component', MySampleComponent);
```
* ## Setting up your components default strings
*
* `translate()`, the base method most developers will use, will always fallback
* to `this.__localeDefaultStrings` when a string cannot be located in the
* localeStrings language code. `this.__localeDefaultStrings` is an object
* with _no language code_ definition:

```javascript
this.__localeDefaultStrings = {
  hiThere: 'hello there, I am a string!',
  interpolateMe: 'what if {{ thing }} is {{ other }}',
  myComp: {
       internalBlock: {
         otherBlock: {
           sampleString: 'Hello World'
        }
      }
    }
  },
};
```
 * This should be set in your properties or within the constructor before the
 *`connectedCallback()` lifecycle is triggered.
 *
 * ## Understanding the properties
 *
 * translation() adds four public properties to your class that you _should not_
 * mutate.
 *
 * * **localeStrings**: strings separated into standard language code arrays
 * * **localePath**: file path to where locale translation json files are stored
 * * **localeCustomPath**: folder path to use in conjunction with localePath to load for translation languageCode files.
 * * **localeFolder**: element folder name to use in conjunction with localePath to load for translation languageCode files. Overrides this.tagName.toLocaleLowerCase().
 * * **localeAddOns**: Array of names to use in conjunction with localePath to load for translation languageCode baselines files such as Awards. Does not use the element this.tagName.toLocaleLowerCase() within the path.
 * * **locale**: standard language code of the current selected language
 * * **mode**: the operating mode of the component, allowing translation to be managed
 *
 * **DANGER**: you should not manage these things or set them as attributes
 * except in the setup case (either as the base standalone component or the
 * shell). If you are using this class, keep reading below to understand how
 * string translation works.
 *
 * ## Translating a string
 *
 * translation() adds a public method called `translate()` to your class. You
 * can use this class to translate strings on the fly based on locale changes
 * with no manual intervention.
 *
 * _Polymer 3_
 ```
 [[translate('sampleString', null, locale)]]
 ```
 *
 * _LitElement_
 ```
 ${this.translate('sampleString', null, this.locale)}
 ```
 *
 * ## Translating a deep object string
 *
 * Sometimes we have a translation string that is very deep in the object for a
 * given locale or component. An example object might look like this:
 ```
 this.localeString = {
   ['en-us']: {
     myComp: {
       internalBlock: {
         otherBlock: {
           sampleString: 'Hello World'
        }
      }
    }
  }
 ```
 * Due to it's design, no special effort is required to access this deep object
 * string. `translate()` does the work for you:
 *
 * _Polymer 3_
 ```
 [[translate('myComp.internalBlock.otherBlock.sampleString', null, locale)]]
 ```
 *
 * _LitElement_
 ```
 ${this.translate('myComp.internalBlock.otherBlock.sampleString', null, this.locale)}
 ```
 *
 * ## Interpolation
 *
 * `translate()` also gives us the ability to use the second param within its
 * signature to allow interpolation for string replacement.
 *
 * Replacement strings are denoted by `{{ someString  }}` format within your
 * localeStrings:
 ```
 this.localeString = {
   ['en-us']: {
     myReplaceString: 'Hi {{ replaceme }}! You can do {{ other }}!',
     anotherString: 'anything'
   }
  }
 ```
 *
 * _Polymer 3_
 * Note: due to how Polymer's `get template()` and bind system works, you can't directly inject an object
 * into the param. You will have to do so implicitly in another function or variable:
 *
 ```
myInterpolate() {
  return {
    replaceme: 'I am a replacement',
    other: this.translate('anotherString', null, this.locale)
  }
}
static get template() {
  return html`
    <p>[[translate('myReplaceString', myInterpolate, locale)]]</p>
  `;
}
```
 *
 * _LitElement_
 *
 * Unlike Polymer 3, litElement allows direct injection via the template string:
 *
 ```
<div>
  ${this.translate(
    'myReplaceString',
    {
      replaceme: 'I am a replacement',
      other: this.translate('anotherString', null, this.locale),
    },
    this.locale,
  )}
</div>
 ```
 *
 * @polymer
 * @mixinFunction
 */
const internalTranslation = (base) =>
  class extends base {
    static get properties() {
      return {
        /**
         * Strings separated into standard language code arrays
         * @type {object}
         * @default {}
         */
        localeStrings: Object,
        /**
         * File path to where locale translation json files are stored
         * @type {string}
         * @default ''
         */
        // @ts-ignore
        localePath: String,
        /**
         * Folder path to use in conjunction with localePath to load for
         * translation languageCode files.
         * @type {string}
         * @default null
         */
        // @ts-ignore
        localeCustomPath: String,
        /**
         * Element folder name to use in conjunction with localePath to load for
         * translation languageCode files. Overrides
         * this.tagName.toLocaleLowerCase().
         * @type {string}
         * @default null
         */
        // @ts-ignore
        localeFolder: String,
        /**
         * Array of names to use in conjunction with localePath to load for
         * translation languageCode baselines files such as Awards. Does not use
         * the element this.tagName.toLocaleLowerCase() within the path.
         * @type {array}
         * @default []
         */
        // @ts-ignore
        localeAddOns: Array,
        /**
         * standard language code of the current selected language
         * @type {string}
         * @default en-us
         */
        // @ts-ignore
        locale: String,
        /**
         * the operating mode of the component, allowing translation to be
         * managed
         * @type {string} shell|standalone
         * @default standalone
         */
        // @ts-ignore
        mode: String,
      };
    }

    constructor() {
      super();
      this.locale = "en-us";
      this.localePath = "";
      this.localeAddOns = [];
      this.localeCustomPath = "";
      this.localeFolder = null;
      this.localeStrings = {};
      this.mode = "shell";
      this.__memorizedCache = {
        eventListener: null,
      };
    }

    async connectedCallback() {
      // Must run first - lit-element v3's connectedCallback is what attaches the
      // shadow root (createRenderRoot happens here, not at construction like v2).
      // __localeChanged below can call into shadow-DOM-reading code
      // (__repaintGrid -> __getDomRef), which needs the shadow root to already exist.
      super.connectedCallback();

      // Pulls the top level overrides that were fed into our shell/component on
      // boot; these are languageCode specific
      // @ts-ignore
      this.localeStrings = {
        ...this.localeStrings,
        ...store.getState().locale.stringsOverride,
      };

      // Did we just come into view via a dynamic import? We should probably get
      // our strings
      const { locale } = store.getState();
      const langCode = locale.languageCode;

      // We don't expect this mutate from the store
      this.localePath = locale.path;

      if (this.locale !== langCode) {
        const event = {
          detail: {
            languageCode: langCode,
          },
        };
        await this.__localeChanged(event);
      }

      // listen to top level languageCode changes from the boot system
      this.__memorizedCache.eventListener = this.__localeChanged.bind(this);
      document.addEventListener("locale-updated", this.__memorizedCache.eventListener, false);
    }

    disconnectedCallback() {
      super.disconnectedCallback();

      // be a good browser citizen, clean up
      document.removeEventListener("locale-updated", this.__memorizedCache.eventListener, false);
    }

    /**
     * An observed attribute fires the custom elements API callback
     * attributeChangedCallback whenever it changes.
     * @param {string} name attribute name
     * @param {string} oldVal previous value
     * @param {string} newVal new value
     */
    attributeChangedCallback(name, oldVal, newVal) {
      if (this.mode === "standalone") {
        switch (name) {
          case "locale": {
            setLocaleLanguageCode({ languageCode: newVal });
            break;
          }
          default:
            break;
        }
      }
      super.attributeChangedCallback(name, oldVal, newVal);
    }

    /**
     * Our locale handler to run workloads
     * @param {object} event
     */
    async __localeChanged(event) {
      // feed the remote grabber before setting the languageCode to the locale
      // otherwise we'll be too fast and have to run another render update
      await this.__localeRemote(event.detail.languageCode);

      this.locale = event.detail.languageCode;
    }

    /**
     * Fetches remote locale files for a given component. Will merge with
     * localeStrings for override effect.
     * @param {string} lang Language code
     * @private
     */
    async __localeRemote(lang) {
      try {
        if (this.mode !== "standalone" || this.localePath !== "") {
          const folderName = this.localeFolder || this.tagName.toLocaleLowerCase();
          const responses = await Promise.all([
            fetchDedupe(`${this.localePath}${this.localeCustomPath}${folderName}/${lang}.json`).then((res) => res.data),
            ...this.localeAddOns.map((addon) =>
              fetchDedupe(`${this.localePath}${addon}/${lang}.json`).then((res) => res.data),
            ),
          ]);

          // creates the override effect
          const strings = {
            [lang]: {
              ...responses.reduce((acc, val) => ({ ...acc, ...val }), {}),
              ...this.localeStrings[lang],
            },
          };

          this.localeStrings = { ...this.localeStrings, ...strings };
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(error);
        // maybe we're missing a file or we have an edge case, either way, we
        // don't throw because components are expected to have their default
        // strings as the fallback
      }
    }

    /**
     * extracts the passed into the function, or runs the function to get a
     * return value to inject
     * @static
     * @param {function | string} obj
     * @returns {string}
     */

    static $__translateExtract(obj) {
      return typeof obj === "function" ? obj() : obj;
    }

    /**
     * replace our placeholders, interpolate our values
     * @static
     * @param {string} text
     * @param {array} values
     * @returns {string}
     */

    static $__translateInterpolate(text, values) {
      // @ts-ignore
      return Object.entries(this.$__translateExtract(values)).reduce(
        (text, [key, value]) => text.replace(new RegExp(`{{[  ]*${key}[  ]*}}`, "ig"), this.$__translateExtract(value)),
        text,
      );
    }

    /**
     * return the proper string from the passed object via dot notation
     * @static
     * @param {string} path
     * @param {object} object
     * @returns {string}
     */

    static $__translateGet(path, object) {
      return path.split(".").reduce((ac, cv) => (ac && ac[cv] !== undefined ? ac[cv] : null), object);
    }

    /**
     * translates a given key, overrideType, override value and interpolates values regardless of position. will only be called for overriding the default translation
     * locale causes a re-translate in views.
     * @param {string} key
     * @param {object} replace
     * @param {string} locale
     * @param {string} overrideType
     * @param {string} override
     * @return {string}
     */
    translateOverride(key, replace, locale, overrideType, override) {
      const overrideKey = `__overrides.${overrideType}.${override}.${key}`;
      let finalKey;
      if (overrideType && override) {
        if (
          Object.hasOwnProperty.call(this.localeStrings, locale) &&
          Object.keys(this.localeStrings[locale]).length !== 0
        ) {
          finalKey = this.constructor.$__translateGet(overrideKey, this.localeStrings[locale]) ? overrideKey : key;
        } else {
          finalKey = this.constructor.$__translateGet(overrideKey, this.__localeDefaultStrings) ? overrideKey : key;
        }
      } else {
        finalKey = key;
      }
      return this.translate(finalKey, replace, locale);
    }

    /**
     * translates a given key and interpolates values regardless of position.
     * locale causes a re-translate in views.
     * @param {string} key
     * @param {object} replace
     * @param {string} locale
     * @return {string}
     */
    translate(key, replace, locale) {
      // find out translation in localeStrings, otherwise fallback to the
      // default strings (which we never merge)
      let translation;
      if (
        Object.hasOwnProperty.call(this.localeStrings, locale) &&
        // @ts-ignore
        this.constructor.$__translateGet(key, this.localeStrings[locale])
      ) {
        // @ts-ignore
        translation = this.constructor.$__translateGet(key, this.localeStrings[locale]);
      } else {
        // @ts-ignore
        translation = this.constructor.$__translateGet(key, this.__localeDefaultStrings);
      }

      // do we have replacements we want to do? Go get extracts so we can use them.
      // @ts-ignore
      const values = replace != null ? this.constructor.$__translateExtract(replace) : null;

      // if we have values to replace, interpolate them into our translation
      const output =
        values != null
          ? // @ts-ignore
            this.constructor.$__translateInterpolate(translation, values)
          : translation;

      if (output) {
        return output;
      }
      return "";
    }
  };

export const translation = internalTranslation;
